import React, { useEffect, useState } from 'react';
import { MessageCircle, ExternalLink } from 'lucide-react';
import {
  whatsappInboxService, WhatsAppConversation,
} from '@/services/integrations/whatsappInboxService';
import { WhatsAppSocketProvider } from '@/hooks/useWhatsAppSocket';
import { useUser } from '@/contexts/UserContext';
import ChatPanel from '@/pages/whatsapp-inbox/_components/ChatPanel';
import StartConversationModal from '@/pages/whatsapp-inbox/_components/StartConversationModal';

interface LeadWhatsAppTabProps {
  leadUuid: string;
  leadName?: string | null;
}

/**
 * WhatsApp tab for the lead edit modal — the real ChatPanel (same header,
 * auto-scroll-to-latest, full composer with text/template/media/notes,
 * window-status banner) rather than a parallel read-only rebuild, so this
 * gets full messaging capability and stays in lockstep with the standalone
 * Inbox for free instead of accumulating its own drift.
 *
 * ChatPanel depends on WhatsAppSocketProvider for live updates, which
 * normally exists only once at the top of the Inbox page — this modal is a
 * separate render tree, so it needs its own provider instance (its own
 * short-lived socket connection) rather than crashing with "must be used
 * within a WhatsAppSocketProvider".
 */
const LeadWhatsAppTab: React.FC<LeadWhatsAppTabProps> = (props) => (
  <WhatsAppSocketProvider>
    <LeadWhatsAppTabInner {...props} />
  </WhatsAppSocketProvider>
);

const LeadWhatsAppTabInner: React.FC<LeadWhatsAppTabProps> = ({ leadUuid, leadName }) => {
  const { userData } = useUser();
  const currentUserName = userData?.data?.userName || 'Agent';

  const [conversation, setConversation] = useState<WhatsAppConversation | null>(null);
  const [lookupLoading, setLookupLoading] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);

  useEffect(() => {
    setLookupLoading(true);
    whatsappInboxService.listConversations({ lead_uuid: leadUuid, page_size: 1 })
      .then((res) => setConversation(res.data[0] || null))
      .finally(() => setLookupLoading(false));
  }, [leadUuid]);

  if (lookupLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
        <span className="ml-2 text-gray-600">Loading WhatsApp conversation...</span>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="p-4">
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-400 mb-3"><MessageCircle size={48} className="mx-auto" /></div>
          <p className="text-gray-600 font-medium text-lg mb-1">No WhatsApp Conversation Yet</p>
          <p className="text-gray-500 text-sm mb-4">Messages with this lead will appear here once a conversation starts</p>
          <button
            onClick={() => setShowStartModal(true)}
            className="px-4 py-2 rounded-lg bg-brandColor text-white text-[13px] font-bold hover:opacity-90"
          >
            Start WhatsApp conversation
          </button>
        </div>

        {showStartModal && (
          <StartConversationModal
            lead={{ uuid: leadUuid, name: leadName }}
            onClose={() => setShowStartModal(false)}
            onResolved={setConversation}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden border-t border-gray-100">
      <div className="flex justify-end px-4 py-1.5 border-b border-gray-100 bg-white flex-shrink-0">
        <a
          href={`/whatsapp-inbox?lead=${leadUuid}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-[11px] font-semibold text-brandColor hover:opacity-80"
        >
          <ExternalLink className="w-3 h-3" /> Open in full Inbox
        </a>
      </div>
      <ChatPanel
        key={conversation.uuid}
        conversation={conversation}
        currentUserName={currentUserName}
        onConversationPatch={(patch) => setConversation((prev) => (prev ? { ...prev, ...patch } : prev))}
      />
    </div>
  );
};

export default LeadWhatsAppTab;
